import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import { Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { useQuietBanner } from '../../hooks/logic/useHeldAlerts';
import { ButtonLink } from '../shared/ButtonLink';

import { hasHeld, quietBannerLine } from './quietText';
import type { ShownQuietBanner } from './quietText';

export const HeldAlertsUnknown: React.FC = () => {
  const { t } = useTranslation();
  return (
    <InlineAlert
      severity="warning"
      message={t(
        'closeTheLoop.quietHours.heldUnknown',
        'Couldn’t check what is being held right now. Your quiet hours are still in force.'
      )}
    />
  );
};

const MOON = <NightsStayOutlinedIcon fontSize="small" />;
const actionsSx = { flexWrap: 'wrap' } as const;

const bannerActionsOf = (t: TFunction, heldTarget: string | null): React.ReactElement | undefined => {
  if (heldTarget === null) return undefined;
  return (
    <Stack direction="row" spacing={2} useFlexGap sx={actionsSx}>
      <ButtonLink intent="tertiary" to={heldTarget}>
        {t('closeTheLoop.quietHours.seeHeld', 'See held alerts')}
      </ButtonLink>
    </Stack>
  );
};

interface QuietHoursBannerProps {
  readonly banner: ShownQuietBanner;
  readonly to?: string;
  readonly sx?: SxProps<Theme>;
}

export const QuietHoursBanner: React.FC<QuietHoursBannerProps> = ({ banner, to, sx }) => {
  const { t } = useTranslation();
  const heldTarget = to !== undefined && hasHeld(banner.counts) ? to : null;
  return (
    <InlineAlert role="status" severity="info" icon={MOON} sx={sx} message={quietBannerLine(t, banner)} action={bannerActionsOf(t, heldTarget)} />
  );
};

export interface HeldAlertsBannerProps {
  readonly to?: string;
  readonly sx?: SxProps<Theme>;
}

export const HeldAlertsBanner: React.FC<HeldAlertsBannerProps> = ({ to, sx }) => {
  const banner = useQuietBanner();
  if (banner.kind === 'hidden') return null;
  return <QuietHoursBanner banner={banner} to={to} sx={sx} />;
};
